import { Cell } from "./Cell";
import { ApplicantOrInvitee } from "../types";
import { Show } from "shared/components/helpers/Show";
import { Link } from "react-router-dom";
import { useAuthContext } from "providers/Auth";
import {
  ApplicantStatus,
  ApplicationStatus,
  Invitee,
  PartnerFeatures
} from "shared/types";
import { Dispatch, SetStateAction } from "react";
import { getFullName } from "shared/utils/get-full-name";
import { compactNumber, formatPhone } from "shared/utils";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize
} from "shared/components";
import { useApplicantChatUnreads } from "./useApplicantChatUnreads";
import { CopyableContactRow } from "./CopyableContactRow";
import { hasActionInManualApplication } from "views/applications-id/helpers";
import { BubbleChatIcon } from "@hugeicons-pro/core-stroke-rounded";
import { getApplicantStatus } from "utils/get-applicant-status";
import { ApplicationActionsMenu } from "shared/components/ApplicationActionsMenu";
import { ApplicationActionsMenuVariant } from "shared/components/ApplicationActionsMenu/types";
import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";

const getOptionalStringField = (
  obj: object,
  key: string
): string | undefined => {
  return key in obj
    ? (((obj as Record<string, unknown>)[key] as string) ?? undefined)
    : undefined;
};

const resolveNameFields = (
  applicant: ApplicantOrInvitee,
  pendingMagicLink?: Invitee
) => ({
  applicantFirstName: getOptionalStringField(applicant, "first_name"),
  applicantLastName: getOptionalStringField(applicant, "last_name"),
  applicantMiddleName: getOptionalStringField(applicant, "middle_name"),
  inviteFirstName: pendingMagicLink
    ? getOptionalStringField(pendingMagicLink, "first_name")
    : undefined,
  inviteLastName: pendingMagicLink
    ? getOptionalStringField(pendingMagicLink, "last_name")
    : undefined,
  inviteMiddleName: pendingMagicLink
    ? getOptionalStringField(pendingMagicLink, "middle_name")
    : undefined
});

const getApplicantNameOrEmail = (
  applicant: ApplicantOrInvitee,
  pendingMagicLink?: Invitee
) => {
  const {
    applicantFirstName,
    applicantLastName,
    applicantMiddleName,
    inviteFirstName,
    inviteLastName,
    inviteMiddleName
  } = resolveNameFields(applicant, pendingMagicLink);

  const hasApplicantName = Boolean(
    applicantFirstName || applicantLastName || applicantMiddleName
  );
  const hasInviteName = Boolean(
    inviteFirstName || inviteLastName || inviteMiddleName
  );

  if (hasApplicantName && applicantFirstName && applicantLastName)
    return getFullName({
      firstName: applicantFirstName,
      lastName: applicantLastName,
      middleName: applicantMiddleName,
      short: true
    });

  if (hasInviteName && inviteFirstName && inviteLastName)
    return getFullName({
      firstName: inviteFirstName,
      lastName: inviteLastName || pendingMagicLink?.email || "",
      middleName: inviteMiddleName || undefined,
      short: true
    });

  return pendingMagicLink?.email || applicant.email;
};

export const ApplicantHeaderCell = ({
  applicant,
  applicationId,
  pendingMagicLink,
  setCancelApplicantModal,
  setUncancelApplicantModal,
  setChangeRoleModal,
  setSetPrimaryModal,
  setRefundModal,
  setResendInviteModal,
  setDocumentRequestModal,
  applicationStatus,
  allowCoSigner,
  allowOccupant,
  setManualApplicationOpen
}: {
  applicant: ApplicantOrInvitee;
  applicationId?: string;
  pendingMagicLink?: Invitee;
  setCancelApplicantModal: Dispatch<SetStateAction<string | undefined>>;
  setUncancelApplicantModal: Dispatch<SetStateAction<string | undefined>>;
  setChangeRoleModal: Dispatch<SetStateAction<ApplicantOrInvitee | undefined>>;
  setSetPrimaryModal: Dispatch<SetStateAction<ApplicantOrInvitee | undefined>>;
  setRefundModal: Dispatch<SetStateAction<string | undefined>>;
  setDocumentRequestModal: Dispatch<SetStateAction<string | undefined>>;

  setResendInviteModal?: Dispatch<SetStateAction<Invitee | undefined>>;
  applicationStatus?: ApplicationStatus;
  allowCoSigner: boolean;
  allowOccupant: boolean;
  setManualApplicationOpen: (applicantId: number) => void;
}) => {
  const { permissions, type, partnerFeatures } = useAuthContext();
  const { adminPrivileges } = type;
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: disable chat for manual applicants
  const hasChat = partnerFeatures.includes(PartnerFeatures.Chat);

  const chatUnreads = useApplicantChatUnreads({
    applicant,
    hasChat,
    isNativeChat: type.isNativeChat
  });

  const isApplicant = "verifications" in applicant;
  const isManual = "is_manual" in applicant && applicant.is_manual;

  const effectiveStatus = getApplicantStatus({
    applicant,
    pendingMagicLink
  });

  const chatLinkTo = isApplicant
    ? `/applications/${applicationId}/reports?applicantId=${applicant.id}&report=chat`
    : "";

  const displayName = getApplicantNameOrEmail(applicant, pendingMagicLink);

  const contactEmail = pendingMagicLink?.email || applicant.email;
  const contactPhone =
    pendingMagicLink?.phone || getOptionalStringField(applicant, "phone");

  return (
    <Cell className="!h-[250px] md:!py-6 relative w-full" isDataCell>
      <div className="w-full">
        <div className="flex justify-between items-start gap-2">
          <div className="min-w-0">
            <ApplicationStatusBadge
              type="applicant"
              status={effectiveStatus}
              isAutoCanceled={
                "canceled_by_auto_cancel" in applicant &&
                !!applicant.canceled_by_auto_cancel
              }
            />
            <h3 className="text-base leading-6 font-bold text-content-1000 truncate max-w-[240px] mt-2">
              {displayName}
            </h3>
            <p className="text-xs font-normal text-content-400 truncate max-w-[240px] mt-1">
              {isManual ? "Manual application" : "Self-serve application"}
            </p>
          </div>
          <ApplicationActionsMenu
            variant={ApplicationActionsMenuVariant.Group}
            contentClassName="z-[12]"
            triggerClassName="border-none !shadow-none"
            borderLess={false}
            applicant={applicant}
            pendingMagicLink={pendingMagicLink}
            onCancelClick={(applicantId) =>
              setCancelApplicantModal(applicantId)
            }
            onUncancelClick={(applicantId) =>
              setUncancelApplicantModal(applicantId)
            }
            onChangeRoleClick={(applicant) =>
              setChangeRoleModal(applicant as ApplicantOrInvitee)
            }
            onSetPrimaryClick={(applicant) =>
              setSetPrimaryModal(applicant as ApplicantOrInvitee)
            }
            onDocumentRequest={(applicantId) =>
              setDocumentRequestModal(applicantId)
            }
            onRefundClick={(applicantId) => setRefundModal(applicantId)}
            onResendInviteClick={(invite) => setResendInviteModal?.(invite)}
            applicationStatus={applicationStatus}
            allowCoSigner={allowCoSigner}
            allowOccupant={allowOccupant}
            hasAdminPrivileges={adminPrivileges}
            permissions={{
              canChangeApplicantRole: permissions.canChangeApplicantRole,
              canCancelApplications: permissions.canCancelApplications,
              canResendInvitation: permissions.canResendInvitation,
              canRefundApplication: permissions.canRefundApplication
            }}
          />
        </div>
        <div className="border-t border-alpha-40 mt-3 pt-3 space-y-2">
          <Show if={Boolean(contactEmail)}>
            <CopyableContactRow
              value={contactEmail || ""}
              copyAriaLabel="Copy email address"
            />
          </Show>
          <Show if={Boolean(contactPhone)}>
            <CopyableContactRow
              value={contactPhone || ""}
              displayValue={formatPhone(contactPhone || "")}
              copyAriaLabel="Copy phone number"
            />
          </Show>
        </div>

        {isManual &&
        !(
          effectiveStatus &&
          [ApplicantStatus.Canceled, ApplicantStatus.Submitted].includes(
            effectiveStatus
          )
        ) &&
        hasActionInManualApplication({
          ...applicant,
          verifications: applicant.verifications.flat()
        }) ? (
          <Button
            size={ButtonSize.SM}
            variant={ButtonVariant.PRIMARY}
            className="mt-4 w-full"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setManualApplicationOpen(Number(applicant.id));
            }}
          >
            {applicant.application_started_at
              ? "Complete application"
              : "Start application"}
          </Button>
        ) : null}
        {isApplicant &&
        !(
          applicant.is_manual &&
          [
            ApplicantStatus.Pending,
            ApplicantStatus.Finished,
            ApplicantStatus.Started
          ].includes(applicant.application_status)
        ) ? (
          <div className="flex items-center gap-2 mt-4">
            <Button size={ButtonSize.SM} className="w-full">
              Open application
            </Button>
            <Show
              if={
                hasChat &&
                isApplicant &&
                !applicant.is_manual &&
                applicant.application_started_at
              }
            >
              <Link to={chatLinkTo} className="relative shrink-0 block">
                <IconButton
                  HugeIcon={BubbleChatIcon}
                  size={IconButtonSize.SM}
                  className="block"
                />
                {chatUnreads ? (
                  <div className="bg-primary-500 text-white rounded text-xxs px-1 ml-auto min-w-[16px] text-center absolute -top-1.5 -right-1 outline outline-white">
                    {compactNumber(chatUnreads)}
                  </div>
                ) : null}
              </Link>
            </Show>
          </div>
        ) : null}
      </div>
    </Cell>
  );
};
